import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { cn } from "../lib/utils";

/**
 * UserAvatar - Standardized user avatar component
 * Consistent avatar display across all interfaces
 */
const UserAvatar = ({
    username,
    imageUrl,
    size = 'medium',
    color = 'primary',
    showStatus = false,
    status = 'offline',
    className
}) => {
    const sizeMap = {
        small: "h-8 w-8 text-xs",
        medium: "h-10 w-10 text-sm",
        large: "h-14 w-14 text-base",
        xlarge: "h-20 w-20 text-xl"
    };

    const colorVariants = {
        primary: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200",
        secondary: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200",
        success: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200",
        warning: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200",
        error: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200",
        info: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-200",
        default: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200"
    };

    // Generate consistent color based on username
    const getRandomColorClass = (str) => {
        if (!str) return colorVariants.default;
        const colors = ['primary', 'secondary', 'success', 'info', 'warning'];
        const index = str.charCodeAt(0) % colors.length;
        return colorVariants[colors[index]];
    };

    const bgColorClass = color === 'random' ? getRandomColorClass(username) : (colorVariants[color] || colorVariants.default);

    // Get initial
    const initial = username ? username.charAt(0).toUpperCase() : '?';

    const renderStatus = () => {
        if (!showStatus) return null;

        const statusColor = status === 'online' ? 'bg-green-500' : 'bg-gray-400';

        return (
            <span className={cn(
                "absolute bottom-0 right-0 block h-3 w-3 rounded-full ring-2 ring-white dark:ring-slate-950",
                statusColor
            )} />
        );
    };

    return (
        <div className="relative inline-block">
            <Avatar className={cn(sizeMap[size] || sizeMap.medium, className)}>
                <AvatarImage src={imageUrl} alt={username} />
                <AvatarFallback className={cn("font-bold", bgColorClass)}>
                    {initial}
                </AvatarFallback>
            </Avatar>
            {renderStatus()}
        </div>
    );
};

export default UserAvatar;
